<div *ngIf="clickedProperty && hoveredDates" fxLayout="column" fxFlexFill>
  <div class="sidebar-header" fxLayout fxLayoutAlign="start center" fxLayoutGap="8px">
    <ng-container *ngIf="!multipleProperties?.length">
      <img class="property-image" [src]="clickedProperty.picture" />
      <span fxFlex class="ellipsis night strong">{{ clickedProperty.name }}</span>
    </ng-container>

    <ng-container *ngIf="multipleProperties?.length">
      <span fxFlex class="ellipsis night strong">{{ multipleProperties?.length }} properties selected</span>
    </ng-container>
    <sbnb-close (click)="triggerClose()"></sbnb-close>
  </div>

  <div class="calendar-sidebar-content" fxFlex>
    <div *sbnbCSDebug="hoveredDates?.length > 1 && multiDayFailure" class="failures-container">
      <span>This date selection contains failures from pushing data to one or more underlying platforms.</span>

      <span style="display: block; margin-top: 12px">
        <a
          class="strong"
          href="https://my.hospitable.com/support-documentation/article/5523151-why-did-my-calendar-updates-fail"
          target="_blank">
          Why did this happen?
        </a>
      </span>
    </div>

    <div
      *sbnbCSDebug="hoveredDates.length === 1 && !multipleProperties?.length && failures?.length > 0"
      class="failures-container">
      <div *ngFor="let failure of failures">
        Failed to push {{ failureTypes[failure.type] }} to {{ failure.platform | platformLabel }}.
      </div>

      <span style="display: block; margin-top: 12px">
        <a
          class="strong"
          href="https://my.hospitable.com/support-documentation/article/5523151-why-did-my-calendar-updates-fail"
          target="_blank">
          Why did this happen?
        </a>
      </span>
    </div>
    <div class="my-2 mx-4" *ngIf="!multipleProperties?.length && clickedProperty?.calendar_restricted">
      <sbnb-alert-card context="info" class="items-center">
        <a
          href="https://help.hospitable.com/en/articles/9972015-my-hospitable-calendar-is-limited"
          class="link-muted !text-night flex items-center gap-2">
          <sbnb-icon icon="alert-circle"></sbnb-icon>
          <span>This calendar is limited.</span>
        </a>
      </sbnb-alert-card>
    </div>

    <div
      class="calendar-sidebar-dates-container"
      *ngIf="perms$.hasAccessTo(Features.PERM_CALENDAR_UPDATE_AVAILABILITY) | async">
      <div class="rounded-lg border border-smoke overflow-hidden">
        <div class="p-3">
          <button
            class="w-full flex justify-between items-center"
            [matMenuTriggerFor]="dateMenu"
            #trigger="matMenuTrigger">
            <span class="text__large strong">
              {{
                (hoveredDates[0] | date: 'MMM d') +
                  (hoveredDates[hoveredDates.length - 1] !== hoveredDates[0]
                    ? ' - ' + (hoveredDates[hoveredDates.length - 1] | date: 'MMM d')
                    : '')
              }}
            </span>
            <sbnb-icon icon="small-arrow-bottom-dark"></sbnb-icon>
          </button>
        </div>
        <div class="bg-smoke-lighter p-3 border-t border-smoke" *ngIf="shouldShowDaySelection">
          <sbnb-day-toggle-group
            [days]="daysToApplyChangesOnSave"
            [startWeekOnSunday]="startOfWeekPreference === 0"
            [useStrikethrough]="true"
            size="small"
            (selected)="updateDaySelection($event)"></sbnb-day-toggle-group>
        </div>
      </div>
      <mat-menu #dateMenu="matMenu" class="filters__cdk-menu">
        <div>
          <sbnb-date-picker
            [numMonths]="1"
            [rangeSelect]="true"
            [checkInOutStyling]="false"
            [showDurationLabels]="false"
            (click)="$event.stopPropagation()"
            (datesSelected)="trigger.closeMenu(); sidebarDatesChanged($event)"></sbnb-date-picker>
        </div>
      </mat-menu>

      <div class="form-group mb-3 mt-4" [formGroup]="availabilityForm">
        <sbnb-toggle-pills
          [labels]="availableLabels"
          [value]="availabilityForm.get('availability').value"
          (changed)="setToggleControl(availabilityForm.get('availability'), $event)"
          [disabled]="saving || readonly || availabilityForm.get('availability').disabled"
          [matTooltip]="readonly ? readonlyTooltip : null"></sbnb-toggle-pills>
        <sbnb-source-label
          *ngIf="availabilityForm.get('availability').value"
          [sourceData]="sourceData"
          [type]="sourceTypeEnum.Availability"
          [propertyId]="clickedProperty.id"
          tooltipPosition="above"></sbnb-source-label>
        <div class="flex flex-col gap-3">
          <mat-error *ngIf="availabilityForm?.errors?.['requireBothCICO']">
            Check-in and checkout availability must both have a value.
          </mat-error>
          <div class="flex items-center justify-between" [matTooltip]="restrictedCICOTooltip('availabilityForCheckin')">
            <label
              class="text__small text-night"
              [class.text-smoke-darker]="availabilityForm.get('availabilityForCheckin').disabled">
              Check-in available
            </label>
            <sbnb-toggle-buttons
              formControlName="availabilityForCheckin"
              [muted]="true"
              [matTooltip]="readonly ? readonlyTooltip : null"></sbnb-toggle-buttons>
          </div>

          <div
            class="flex items-center justify-between"
            [matTooltip]="restrictedCICOTooltip('availabilityForCheckout')">
            <label
              class="text__small text-night"
              [class.text-smoke-darker]="availabilityForm.get('availabilityForCheckout').disabled">
              Checkout available
            </label>
            <sbnb-toggle-buttons
              formControlName="availabilityForCheckout"
              [muted]="true"
              [matTooltip]="readonly ? readonlyTooltip : null"></sbnb-toggle-buttons>
          </div>
          <sbnb-source-label
            [sourceData]="sourceData"
            [type]="sourceTypeEnum.CICOAvailability"
            [propertyId]="clickedProperty.id"></sbnb-source-label>
        </div>
      </div>
      <sbnb-source-label
        *ngIf="!availabilityForm.get('availability').value"
        [sourceData]="sourceData"
        [type]="sourceTypeEnum.Availability"
        [propertyId]="clickedProperty.id"></sbnb-source-label>
    </div>

    <div class="calendar-sidebar-price-container">
      <ng-container *ngIf="perms$.hasAccessTo(Features.PERM_CALENDAR_UPDATE_PRICING_AND_AVAIL) | async">
        <div fxLayout fxLayoutAlign="start center" fxLayoutGap="12px">
          <span fxFlex class="text__xsmall strong">Price</span>
        </div>

        <mat-form-field class="fw">
          <span matPrefix class="prefix-bg">
            <span class="text__small" style="padding-right: 12px">{{ clickedProperty.currency }}</span>
          </span>
          <input
            matInput
            [(ngModel)]="desiredPrice"
            [disabled]="saving || readonly"
            [matTooltip]="readonly ? readonlyTooltip : null"
            [placeholder]="
              placeholderPrices?.length > 1
                ? placeholderPrices[0] + '-' + placeholderPrices[placeholderPrices.length - 1]
                : placeholderPrices
                  ? placeholderPrices[0]
                  : ''
            " />
        </mat-form-field>
        <div class="flex items-center justify-between mb-4">
          <sbnb-source-label
            [sourceData]="sourceData"
            [type]="sourceTypeEnum.Price"
            class="ml-auto"></sbnb-source-label>
        </div>
        <sbnb-dynamic-pricing-sidebar-breakdown
          *ngIf="dynamicPricingUpsellInformation"
          (previewDynamicPricing)="previewDynamicPricing.emit()"
          (useDynamicPricing)="resetToDynamicPricing.emit()"
          [dynamicPricingUpsellInformation]="dynamicPricingUpsellInformation"></sbnb-dynamic-pricing-sidebar-breakdown>

        <!-- hide for multiple property updates -->
        <sbnb-price-preview
          *ngIf="desiredPrice && !multipleProperties?.length"
          [propertyId]="clickedProperty.id"
          [price]="desiredPrice"></sbnb-price-preview>
      </ng-container>

      <ng-container *ngIf="perms$.hasAccessTo(Features.PERM_CALENDAR_UPDATE_AVAILABILITY) | async">
        <div fxLayout fxLayoutAlign="start center">
          <span fxFlex class="text__xsmall strong">Minimum length of stay</span>
        </div>

        <mat-form-field class="fw">
          <input
            matInput
            type="number"
            [disabled]="saving || readonly"
            [matTooltip]="readonly ? readonlyTooltip : null"
            [placeholder]="placeholderLengthOfStays | fn: getPlaceholderLengthOfStays"
            [(ngModel)]="desiredLengthOfStay" />
        </mat-form-field>

        <sbnb-source-label [sourceData]="sourceData" [type]="sourceTypeEnum.MinStay"></sbnb-source-label>
      </ng-container>

      <ng-container *ngIf="maxNightsEnabled && perms$.hasAccessTo(Features.PERM_CALENDAR_UPDATE_AVAILABILITY) | async">
        <div fxLayout fxLayoutAlign="start center">
          <span fxFlex class="text__xsmall strong">Maximum length of stay</span>
        </div>

        <mat-form-field class="fw">
          <input
            matInput
            type="number"
            [disabled]="saving || readonly"
            [matTooltip]="readonly ? readonlyTooltip : null"
            [placeholder]="placeholderMaxLengthOfStays | fn: getPlaceholderMaxLengthOfStays"
            [(ngModel)]="desiredMaxLengthOfStay" />
        </mat-form-field>

        <sbnb-source-label [sourceData]="sourceData" [type]="sourceTypeEnum.MaxStay"></sbnb-source-label>
      </ng-container>
    </div>

    <div class="calendar-sidebar-notes" fxLayout="column" fxLayoutGap="6px" [formGroup]="availabilityForm">
      <span fxFlex class="text__xsmall strong">Notes</span>
      <textarea
        formControlName="note"
        [matTooltip]="readonly ? readonlyTooltip : null"
        class="note-textarea noresize text__small"
        [placeholder]="availabilityForm.get('note').dirty ? '' : notePlaceholder"></textarea>
    </div>

    <div
      *sbnbCSDebug="hoveredDates?.length === 1 && sources?.length > 0"
      style="padding-left: 12px; padding-bottom: 20px; height: 200px">
      <sbnb-cs-debug-item
        *ngFor="let source of sources"
        style="font-size: 12px; margin-top: 8px; display: block"
        [label]="source.type + ' source'"
        [value]="source.source + ', at: ' + source.date"></sbnb-cs-debug-item>
    </div>
  </div>

  <div class="flex xl:flex-col justify-between xl:justify-normal gap-3 border-t border-smoke px-6 py-3 xl:p-6">
    <sbnb-add-manual-booking-button
      class="flex-1 max-w-[60%] xl:max-w-full"
      *ngIf="(perms$.hasAccessTo(Features.PERM_MANAGE_RESERVATIONS) | async) && !this.multipleProperties?.length"
      [type]="'secondary'"
      size="normal"
      [fullWidth]="true"
      [startDate]="hoveredDates[0]"
      [endDate]="hoveredDates[hoveredDates.length - 1]"
      [propertyId]="clickedProperty.id"
      (bookingChanged)="manualBookingAdded($event)"
      (customQuoteCreated)="onCustomQuoteCreated($event)"></sbnb-add-manual-booking-button>

    <sbnb-button
      class="max-w-[40%] xl:max-w-full"
      [disabled]="
        saving ||
        availabilityForm.invalid ||
        (!desiredPrice && !desiredLengthOfStay && !desiredMaxLengthOfStay && !availabilityForm.dirty)
      "
      label="Save"
      loadingLabel="Saving..."
      [loading]="saving"
      [fullWidth]="true"
      (click)="triggerSave()"></sbnb-button>
  </div>
</div>
